// Models
import {Beat, BeatClass, BeatStatus, emptyBeat, PointKey, POINT_KEYS} from "./model"

import {msToSampleZeroBased, sampleToMsZeroBased} from "./globalCsv"
import {beatsFromLeadWaves} from "./perLeadCsv"

/**
 * The annotation endpoint's shape, and the conversion to and from the beats
 * this screen works in.
 *
 * The server keeps ZERO-BASED SAMPLE indices — the convention of the
 * predictions files it imports — and the screen works in milliseconds, so the
 * conversion happens here and nowhere else. Null means the wave is absent on
 * both sides.
 */

export type BeatDto = {
    beatNo: number
    rPeak: number | null
    pStart: number | null
    pEnd: number | null
    qrsStart: number | null
    qrsEnd: number | null
    tStart: number | null
    tEnd: number | null
    beatClass: string | null
    usable: boolean
    uncertain: string[]
    status: string
    comment: string | null
    /** Server-set; ignored on save. */
    editedBy?: string | null
    editedAt?: string | number[] | null
}

export type LeadWaveDto = {
    lead: string
    wave: string
    onset: number
    peak: number | null
    offset: number
}

export type AnnotationDto = {
    beats: BeatDto[]
    leads: LeadWaveDto[]
    /**
     * The rate the samples above are counted at — the ECG's own, set by the
     * server. Must equal the signal's rate, or the positions mean something
     * else; null from a server that does not send it yet.
     */
    sampleRate?: number | null
}

/**
 * What a save sends: the beats, and — to a server that announced its rate on
 * GET — the rate they were converted at. A server that did not announce one
 * refuses the field (its JSON mapper fails on unknown properties).
 */
export type AnnotationSaveDto = {
    sampleRate?: number
    beats: BeatDto[]
}

const BEAT_CLASSES = ["N", "S", "V", "F", "Q"];
const STATUSES = ["auto", "confirmed", "corrected"];

/**
 * When a human last touched the beat, as ISO — or null.
 *
 * Jackson writes a `LocalDateTime` either as an ISO string or, with its
 * default settings, as `[y, m, d, h, min, s, nanos]`; both are accepted.
 */
function isoOf(at: string | number[] | null | undefined): string | null {
    if (!at) {
        return null
    }

    if (typeof at === "string") {
        return at
    }

    const [y, m, d, h, min, s] = at.concat([0, 0, 0, 0, 0, 0]);
    const pad = (n: number) => (n < 10 ? "0" : "") + n;

    return `${y}-${pad(m)}-${pad(d)}T${pad(h)}:${pad(min)}:${pad(s)}`
}

/**
 * The server's annotation as beats, with any per-lead rows attached to the
 * beat whose QRS they belong to, as reference.
 *
 * @param dto
 * @param sampleRate of the record's signal
 */
export function fromDto(dto: AnnotationDto, sampleRate: number): Beat[] {
    const ms = (sample: number | null) => (sample === null || sample === undefined
        ? null
        : sampleToMsZeroBased(sample, sampleRate));

    const beats = (dto.beats || [])
        .slice()
        .sort((a, b) => a.beatNo - b.beatNo)
        .map(row => {
            /* no R peak: the middle of the QRS (or 0) places the beat on screen,
               but rPeakUnset keeps it from being written back as a real one */
            const qrsMid = row.qrsStart !== null && row.qrsEnd !== null
                ? Math.round((row.qrsStart + row.qrsEnd) / 2)
                : 0;
            const beat = emptyBeat(row.beatNo, ms(row.rPeak !== null ? row.rPeak : qrsMid) as number);
            const knownClass = BEAT_CLASSES.indexOf(row.beatClass || "") !== -1;

            POINT_KEYS.forEach(key => {
                beat.points[key] = ms(row[key])
            });

            return Object.assign(beat, {
                /* a class the server did not have is shown as N but written
                   back as it was — null stays null — until someone sets one */
                beatClass: (knownClass ? row.beatClass : "N") as BeatClass,
                classUnset: !knownClass,
                serverClass: knownClass ? undefined : row.beatClass,
                rPeakUnset: row.rPeak === null || row.rPeak === undefined,
                usable: row.usable !== false,
                uncertain: (row.uncertain || []).filter(key => POINT_KEYS.indexOf(key as PointKey) !== -1) as PointKey[],
                status: (STATUSES.indexOf(row.status) !== -1 ? row.status : "auto") as BeatStatus,
                reviewedAt: isoOf(row.editedAt),
                comment: row.comment || "",
            })
        });

    if (!dto.leads || !dto.leads.length) {
        return beats
    }

    /*
     * The per-lead rows carry no beat number, so they are grouped into beats
     * exactly as a per-lead file is, and each group is given to the global
     * beat whose QRS it sits in — the server's beat numbers can differ from
     * the grouping's when the global marks came from a different source.
     */
    const references = beatsFromLeadWaves(dto.leads.map(row => ({
        lead: row.lead,
        wave: row.wave.toUpperCase() as "P" | "QRS" | "T",
        onsetMs: sampleToMsZeroBased(row.onset, sampleRate),
        peakMs: row.peak === null ? null : sampleToMsZeroBased(row.peak, sampleRate),
        offsetMs: sampleToMsZeroBased(row.offset, sampleRate),
    })));

    return beats.map(beat => {
        let best: Beat | null = null;
        let bestD = 150;

        for (let i = 0; i < references.length; i += 1) {
            const d = Math.abs(references[i].rPeakMs - beat.rPeakMs);

            if (d < bestD) {
                bestD = d;
                best = references[i]
            }
        }

        return best ? Object.assign(beat, {reference: best.reference}) : beat
    })
}

/**
 * Beats as the server stores them. Only the global marks travel — the
 * per-lead reference is never written back.
 *
 * @param beats
 * @param sampleRate of the record's signal
 */
export function toDto(beats: Beat[], sampleRate: number, announceRate: boolean = true): AnnotationSaveDto {
    const sample = (ms: number | null) => (ms === null ? null : msToSampleZeroBased(ms, sampleRate));

    const body: AnnotationSaveDto = {
        beats: beats.map(beat => ({
            beatNo: beat.number,
            rPeak: beat.rPeakUnset ? null : sample(beat.rPeakMs),
            pStart: sample(beat.points.pStart),
            pEnd: sample(beat.points.pEnd),
            qrsStart: sample(beat.points.qrsStart),
            qrsEnd: sample(beat.points.qrsEnd),
            tStart: sample(beat.points.tStart),
            tEnd: sample(beat.points.tEnd),
            beatClass: beat.classUnset ? (beat.serverClass === undefined ? null : beat.serverClass) : beat.beatClass,
            usable: beat.usable,
            uncertain: beat.uncertain.slice(),
            status: beat.status,
            comment: beat.comment || null,
        })),
    };

    /* the server refuses a save at any rate but the ECG's — and an older
       server refuses the field itself, so it is sent only when announced */
    if (announceRate) {
        body.sampleRate = sampleRate
    }

    return body
}
